import {
  BasicFormItem,
  Column,
  FormDatepicker,
  FormInputField,
  Row,
  SignatureCanvas,
  Typography,
} from '@/components'
import { ThemeColor } from '@/enums'
import { ShieldCheck, SureinTrustLogo } from '@/icons'
import { useSegment } from '@/modules/analytics'
import { StyledLinkTypography } from '@/pages/SelfAssessmentPage/steps/styles'
import { useContactModal, useFormContext, useSnackbar } from '@/providers'
import { useSignPoa } from '@/services'
import { theme } from '@/theme'
import { themeColor } from '@/utils'
import { Alert } from '@surein/ui'
import { InfoIcon, LockKeyhole } from 'lucide-react'
import { ReactNode, useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useOutletContext, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { StyledRow } from '../styles'
import { isMd, useValidateCheckoutStep } from './helpers'

interface Messages {
  name: string
  birthdate: string
  contactMD: string
  poaAdvisorBubbleText: string
  signPoaErrorHint: string
  general: string
  over18: string
}

const useMessages = (): Messages => {
  const { t } = useTranslation()

  return {
    name: t('name'),
    birthdate: t('birthday'),
    contactMD: t('snackBars.success.contactMD'),
    poaAdvisorBubbleText: t('poaAdvisorBubbleText'),
    signPoaErrorHint: t('signPoaErrorHint'),
    general: t('validations.general'),
    over18: t('validations.over18'),
  }
}

const StyledSSL = styled(Row)`
  width: fit-content;
  padding: 8px;
  background: ${themeColor(ThemeColor.success4)};
  border: 1px solid ${themeColor(ThemeColor.success3)};
  border-radius: 8px;
  margin-bottom: 4px;
`

const StyledDSGVO = styled(Row)`
  width: fit-content;
  padding: 8px;
  background: ${themeColor(ThemeColor.info3)};
  border: 1px solid ${themeColor(ThemeColor.info1)};
  border-radius: 8px;
  margin-bottom: 4px;
`

const StyledLink = styled(StyledLinkTypography)`
  font-weight: 700;
`

const StyledSureinTrustLogo = styled(SureinTrustLogo)`
  width: 32px;
  height: 38px;
`

const NumberedListCounter = styled(Typography).attrs({
  variant: 'description',
  color: ThemeColor.info1,
})`
  border-radius: 100px;
  flex-shrink: 0;
  background: ${themeColor(ThemeColor.info4)};
  height: 24px;
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`

interface NumberedListItemProps {
  counter: string | number
  children: ReactNode
}

const NumberedListItem = ({ counter, children }: NumberedListItemProps) => {
  return (
    <Row gap="16px" alignItems="center">
      <NumberedListCounter>{counter}</NumberedListCounter>
      <Typography variant="description">{children}</Typography>
    </Row>
  )
}

interface FormValues {
  signature?: string
  fullname?: string
  birthdate?: Date
  payment?: {
    paymentId?: string
  }
}

const SignPoaForm = () => {
  const { t } = useTranslation()
  const messages = useMessages()
  const { errors } = useFormContext<FormValues>()
  const { openContact } = useContactModal()

  return (
    <Column gap="24px">
      {errors.signature && (
        <Alert
          icon={InfoIcon}
          iconAlignment="top"
          // @ts-expect-error fix later (or never)
          text={
            <Trans
              components={[
                <StyledLink
                  onClick={openContact}
                  variant="captionB"
                  key="link"
                />,
              ]}
              i18nKey={messages.signPoaErrorHint}
              variant="danger"
            />
          }
          variant="danger"
        />
      )}
      <Column gap="8px">
        <NumberedListItem counter="1">
          {t('checkout.poa.info1')}
        </NumberedListItem>
        <NumberedListItem counter="2">
          {t('checkout.poa.info2')}
        </NumberedListItem>
        <NumberedListItem counter="3">
          {t('checkout.poa.info3')}
        </NumberedListItem>
      </Column>
      <Row gap="16px" alignItems="center">
        <StyledSSL alignItems="center" gap="4px">
          <ShieldCheck color={ThemeColor.b0} />
          <Typography singleLine variant="micro">
            <Trans i18nKey="ssl" />
          </Typography>
        </StyledSSL>
        <StyledSureinTrustLogo />
        <StyledDSGVO alignItems="center" gap="4px">
          <LockKeyhole size={16} color={theme.color.info1} />
          <Typography singleLine variant="micro">
            <Trans i18nKey="gdprCompliant" />
          </Typography>
        </StyledDSGVO>
      </Row>
      <Column gap="4px">
        <BasicFormItem
          component={SignatureCanvas}
          name="signature"
          showErrorHelperText={false}
          noMargin
        />
        <StyledRow>
          <FormInputField
            label={messages.name}
            name="fullname"
            showValidCheck
          />
          <FormDatepicker
            label={messages.birthdate}
            max={new Date()}
            name="birthdate"
            showCalendar={false}
          />
        </StyledRow>
      </Column>
    </Column>
  )
}

interface SignPoaStepProps {
  setButtonState: (state: { onClick?: () => void; loading?: boolean }) => void
  companyId: string
  onNext: () => void
  isLast: boolean
  submitCheckout: (data: { paymentId?: string; items: any[] }) => void
  submitCheckoutLoading: boolean
}

interface OutletContext {
  cart: Record<string, any[]>
  user: any
}

export const SignPoaStep = ({
  setButtonState,
  companyId,
  onNext,
  isLast,
  submitCheckout,
  submitCheckoutLoading,
}: SignPoaStepProps) => {
  const { locationId } = useParams()
  const { cart, user } = useOutletContext<OutletContext>()
  const data = cart[locationId || ''] || []
  const messages = useMessages()
  const snackbar = useSnackbar()
  const { values } = useFormContext<FormValues>()
  const analytics = useSegment()

  const signPoaMutation = useSignPoa(companyId)

  const handleFinishSignPoa = async () => {
    await analytics.track('app_poa_signed')
    if (isLast) {
      submitCheckout({
        paymentId: values?.payment?.paymentId,
        items: data,
      })
    } else {
      onNext()
    }
  }

  const handleSubmit = () => {
    if (isMd(companyId, user)) {
      signPoaMutation.mutate(values, {
        onSuccess: async () => {
          await handleFinishSignPoa()
        },
      })
    } else {
      snackbar.error(messages.contactMD)
    }
  }

  const handleNext = useValidateCheckoutStep(handleSubmit)

  useEffect(() => {
    setButtonState({
      onClick: handleNext,
      loading: signPoaMutation.isLoading || submitCheckoutLoading,
    })

    return () => {
      setButtonState({ loading: false })
    }
  }, [values, signPoaMutation.isLoading, submitCheckoutLoading])

  return <SignPoaForm />
}
